/**
 * Traducción de los errores de Firebase Auth a mensajes para el estudiante.
 *
 * Cierra DEUDA-08 / DEBT-011 (2026-10-03): hasta entonces la UI mostraba el
 * `error.message` crudo del SDK — en inglés y, peor, distinguiendo «correo no
 * registrado» de «contraseña errónea», que permite enumerar cuentas (ASVS V2.9).
 *
 * Regla: TODO error de credenciales produce el MISMO mensaje. Lo que no se
 * reconoce cae al `fallback` que pasa quien llama — nunca al mensaje del SDK.
 * Contrato: `specs/API-CONTRACT/firebase-auth.md` Op A.1 y Op A.8.
 */

export const GENERIC_CREDENTIALS_MESSAGE = 'Correo o contraseña incorrectos.'

const CREDENTIAL_CODES = new Set([
  'auth/invalid-credential',
  'auth/invalid-login-credentials',
  'auth/user-not-found',
  'auth/wrong-password',
  'auth/invalid-email',
  'auth/missing-password',
])

const MESSAGES: Readonly<Record<string, string>> = {
  'auth/too-many-requests': 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.',
  'auth/network-request-failed': 'No hay conexión. Revisa tu red e inténtalo de nuevo.',
  'auth/user-disabled': 'No es posible ingresar con esta cuenta. Escríbenos si crees que es un error.',
  'auth/popup-closed-by-user': 'Se cerró la ventana de Google antes de terminar. Inténtalo de nuevo.',
  'auth/cancelled-popup-request': 'Se cerró la ventana de Google antes de terminar. Inténtalo de nuevo.',
  'auth/popup-blocked':
    'El navegador bloqueó la ventana de Google. Permite las ventanas emergentes de este sitio e inténtalo de nuevo.',
  'auth/operation-not-allowed': 'Este método de ingreso no está disponible en este momento.',
  'auth/unauthorized-domain': 'Este método de ingreso no está disponible en este sitio.',
  'auth/email-already-in-use':
    'No se pudo crear la cuenta con ese correo. Si ya tienes una cuenta, inicia sesión o recupera tu contraseña.',
  'auth/weak-password': 'La contraseña es demasiado débil. Usa al menos 6 caracteres.',
  'auth/credential-already-in-use':
    'Esa cuenta de Google ya está vinculada a otra cuenta de Quizoteca.',
  'auth/account-exists-with-different-credential':
    'Ya existe una cuenta con este correo. Ingresa con tu correo y contraseña.',
}

/** Código `auth/...` de un error del SDK, o `null` si no lo tiene. */
export function getAuthErrorCode(err: unknown): string | null {
  if (typeof err !== 'object' || err === null || !('code' in err)) return null
  const code = (err as { code: unknown }).code
  return typeof code === 'string' ? code : null
}

/** Mensaje en español para mostrar al estudiante. Nunca devuelve el texto del SDK. */
export function mapFirebaseAuthError(err: unknown, fallback: string): string {
  const code = getAuthErrorCode(err)
  if (code === null) return fallback
  if (CREDENTIAL_CODES.has(code)) return GENERIC_CREDENTIALS_MESSAGE
  return MESSAGES[code] ?? fallback
}
